import { isTierZero } from './tierZero';

/**
 * What the shell knows about this machine's assignment, from the redacted host
 * read. `tierId === null` means NO detail at all — the unevaluated state, which
 * is deliberately distinct from an explicit `tier-0` assignment.
 */
export interface HostTierView {
  tierId: string | null;
  nextTier: { tierId: string; name: string } | null;
  hostModeAllowed: boolean;
}

export function toHostTierView(detail: {
  assignedTierId?: string;
  nextTier?: { tierId?: string; name?: string } | null;
  hostModeAllowed?: boolean;
} | null | undefined): HostTierView {
  if (!detail?.assignedTierId) {
    return { tierId: null, nextTier: null, hostModeAllowed: true };
  }
  const next = detail.nextTier;
  return {
    tierId: detail.assignedTierId,
    nextTier: next?.tierId ? { tierId: next.tierId, name: next.name ?? next.tierId } : null,
    // An absent capability means allowed.
    hostModeAllowed: detail.hostModeAllowed !== false,
  };
}

/**
 * Whether the Tier 0 page should be shown.
 *
 * Only a real `tier-0` assignment shows it. A missing detail is the unevaluated
 * state and must NOT be treated as tier-0 — the architecture doc is explicit
 * about that distinction.
 */
export function shouldShowTierZeroPage(view: HostTierView): boolean {
  return isTierZero(view.tierId);
}

/**
 * Whether the ladder has a higher tier to aim at.
 *
 * `nextTier === null` means no locked cards and no learn-more control — there is
 * nothing above this machine.
 */
export function hasHigherTier(view: HostTierView): boolean {
  return view.nextTier !== null;
}
